import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  EOB_CLASSIFICATIONS,
  FINISH_CLASSIFICATION_CATEGORIES,
  FINISH_CLASSIFICATION_CATEGORIES_LEGACY,
  isNonFinancialEob,
} from '~/entities/categories'
import { getDocumentStepUrl } from '~/entities/document'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useAddClassificationReview } from '../api'

export function useSubmitClassificationReview({
  documentKey,
  currentReviewStatus,
  openValidateAndFinishDialog,
}: {
  documentKey: string
  currentReviewStatus: string | null | undefined
  openValidateAndFinishDialog: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const queryParams = useAuditTrailQueryParams()
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')

  const onSuccess = useCallback(
    ({ category }: { category: string | null | undefined }) => {
      queryClient.invalidateQueries(
        apiClient.getDocumentDocumentsKeyGetOptions({
          path: {
            key: documentKey,
          },
        }),
      )
      queryClient.invalidateQueries(
        apiClient.getDocumentClassificationReviewDocumentsKeyClassificationReviewsGetOptions({
          path: { key: documentKey },
        }),
      )
      queryClient.invalidateQueries(
        apiClient.getDocumentExtractedDataDocumentsKeyExtractedDataGetOptions({
          path: { key: documentKey },
        }),
      )
      queryClient.removeQueries(
        apiClient.getDocumentTagsDocumentsKeyTagsGetOptions({
          path: { key: documentKey },
        }),
      )
      queryClient.invalidateQueries(
        apiClient.getDocumentPatientMatchesDocumentsKeyPatientMatchesGetOptions({
          path: { key: documentKey },
        }),
      )
      if (
        (isEobEnabled
          ? FINISH_CLASSIFICATION_CATEGORIES
          : FINISH_CLASSIFICATION_CATEGORIES_LEGACY
        ).includes(category ?? '')
      ) {
        openValidateAndFinishDialog()
      } else {
        const isEob =
          isEobEnabled &&
          EOB_CLASSIFICATIONS.includes(category as (typeof EOB_CLASSIFICATIONS)[number])
        const nextStep = isEob
          ? isNonFinancialEob(category)
            ? 'eob_duplication'
            : 'eob_payment_deposit'
          : 'data'
        navigate(getDocumentStepUrl(documentKey, nextStep, { queryParams }))
      }
    },
    [documentKey, isEobEnabled, navigate, openValidateAndFinishDialog, queryClient, queryParams],
  )

  const { mutateAsync: addClassificationReview, isError } = useAddClassificationReview({
    onSuccess,
  })

  const submitClassificationReview = useCallback(
    async (classificationReview: apiClient.DocumentClassificationReviewCreateRequest) => {
      const isFinishCategory = (
        isEobEnabled ? FINISH_CLASSIFICATION_CATEGORIES : FINISH_CLASSIFICATION_CATEGORIES_LEGACY
      ).includes(classificationReview.document_category ?? '')
      const isCurrentlyFinished =
        isReviewStateResetEnabled &&
        (currentReviewStatus === 'reviewed' || currentReviewStatus === 'auto_reviewed')

      await addClassificationReview({
        path: {
          key: documentKey,
        },
        body: {
          ...classificationReview,
          ...(isFinishCategory
            ? { review_status: 'reviewed' }
            : isCurrentlyFinished
              ? { review_status: 'pending' }
              : {}),
        },
      })
    },
    [
      addClassificationReview,
      currentReviewStatus,
      documentKey,
      isEobEnabled,
      isReviewStateResetEnabled,
    ],
  )

  return {
    submitClassificationReview,
    isClassificationReviewError: isError,
  }
}
